import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  CheckCircle2, AlertTriangle, UserX, Link2, Link2Off, MapPinOff,
  Wrench, RefreshCw, ArrowRight, Trash2, UserPlus, Copy, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI, locationsAPI, employeesAPI } from '../lib/api';

const IssueSection = ({ icon: Icon, color, title, description, items, renderItem, emptyMessage, testId, headerAction }) => (
  <Card data-testid={testId}>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <Icon className={`h-5 w-5 ${color}`} />
        {title}
        <Badge variant={items.length === 0 ? 'secondary' : 'destructive'} className="ml-auto">
          {items.length}
        </Badge>
      </CardTitle>
      <p className="text-xs text-muted-foreground">{description}</p>
      {headerAction}
    </CardHeader>
    <CardContent>
      {items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => renderItem(item, idx))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default function ConsistencyCheck({ embedded = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [locationSelect, setLocationSelect] = useState({});
  const [healScan, setHealScan] = useState(null);
  const [missingIds, setMissingIds] = useState(null);
  const [createdCreds, setCreatedCreds] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [check, locs] = await Promise.all([
        adminAPI.consistencyCheck(),
        locationsAPI.getAll(),
      ]);
      setData(check.data);
      setLocations(locs.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreateLogin = async (item) => {
    if (!window.confirm(
      `Login-Konto für ${item.name} (${item.email}) anlegen?\n\n`
      + 'Es wird ein temporäres Passwort generiert. Der Mitarbeiter muss beim ersten Login '
      + 'Passwort und PIN ändern. Rolle: Mitarbeiter (nur eigene Daten).'
    )) return;
    setBusy((p) => ({ ...p, [`login-${item.employee_id}`]: true }));
    try {
      const res = await employeesAPI.createSystemAccess(item.employee_id, {
        auto_generate_password: true,
        scope_type: 'own',
        branch_ids: [],
        location_ids: [],
      });
      setCreatedCreds({
        name: item.name,
        email: res.data?.email || item.email,
        password: res.data?.generated_password || null,
      });
      toast.success(`Login für ${item.name} erstellt`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen des Logins');
    } finally {
      setBusy((p) => ({ ...p, [`login-${item.employee_id}`]: false }));
    }
  };

  const runAction = async (key, fn, successMsg) => {
    setBusy((p) => ({ ...p, [key]: true }));
    try {
      await fn();
      toast.success(successMsg);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Aktion');
    } finally {
      setBusy((p) => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </MaybeLayout>
    );
  }

  const total = data?.total_issues || 0;

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="consistency-check-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Wrench className="h-6 w-6 text-primary" />
              Daten-Konsistenz-Check
            </h1>
            <p className="text-muted-foreground mt-1">
              Findet Mitarbeiter/User-Verknüpfungsprobleme, die zu Rechteproblemen führen können.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={busy.healScan}
              data-testid="data-heal-scan-btn"
              onClick={async () => {
                setBusy((p) => ({ ...p, healScan: true }));
                try {
                  const res = await adminAPI.dataHealScan();
                  setHealScan(res.data);
                  const b = res.data?.total_broken || 0;
                  toast.success(b > 0
                    ? `${b} fehlerhafte(s) Alt-Dokument(e) gefunden (${res.data?.total_healable || 0} automatisch reparierbar)`
                    : 'Keine fehlerhaften Listen-Dokumente gefunden');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Fehler bei der Diagnose');
                } finally {
                  setBusy((p) => ({ ...p, healScan: false }));
                }
              }}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Listen-Daten prüfen
            </Button>
            <Button
              variant="outline"
              disabled={busy.dataHeal}
              data-testid="data-heal-btn"
              onClick={async () => {
                if (!window.confirm('Fehlende Pflichtfelder in Alt-Datensätzen (z.B. Maßnahmen ohne Farbe) auffüllen? Bestehende Werte bleiben unverändert.')) return;
                setBusy((p) => ({ ...p, dataHeal: true }));
                try {
                  const res = await adminAPI.dataHeal();
                  const n = res.data?.total_healed || 0;
                  toast.success(n > 0 ? `${n} Feld(er) in Alt-Datensätzen bereinigt` : 'Keine Bereinigung nötig – alle Daten konsistent');
                  setHealScan(null);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Fehler bei der Datenbereinigung');
                } finally {
                  setBusy((p) => ({ ...p, dataHeal: false }));
                }
              }}
            >
              <Wrench className="h-4 w-4 mr-2" />
              Daten bereinigen
            </Button>
            <Button
              variant="outline"
              disabled={busy.missingIdsScan}
              data-testid="missing-ids-scan-btn"
              onClick={async () => {
                setBusy((p) => ({ ...p, missingIdsScan: true }));
                try {
                  const res = await adminAPI.missingIdsScan();
                  setMissingIds(res.data);
                  const m = res.data?.total_missing || 0;
                  toast.success(m > 0
                    ? `${m} Datensatz/Datensätze ohne ID-Feld gefunden`
                    : 'Alle Datensätze haben ein gültiges ID-Feld');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Fehler bei der Diagnose');
                } finally {
                  setBusy((p) => ({ ...p, missingIdsScan: false }));
                }
              }}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Datensätze ohne ID prüfen
            </Button>
            <Button variant="outline" onClick={load} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {healScan && (
          <Alert
            data-testid="heal-scan-result"
            className={healScan.total_broken === 0
              ? 'border-green-500/50 bg-green-50 dark:bg-green-900/10'
              : 'border-amber-500/50 bg-amber-50 dark:bg-amber-900/10'}
          >
            {healScan.total_broken === 0
              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
              : <AlertTriangle className="h-4 w-4 text-amber-600" />}
            <AlertTitle>
              {healScan.total_broken === 0
                ? 'Alle Listen-Daten sind valide'
                : `${healScan.total_broken} fehlerhafte(s) Listen-Dokument(e) · ${healScan.total_healable} automatisch reparierbar`}
            </AlertTitle>
            {healScan.total_broken > 0 && (
              <AlertDescription className="space-y-1">
                <p className="text-xs">Diese Dokumente werden aktuell in den Listen stillschweigend übersprungen. „Daten bereinigen" repariert die automatisch behebbaren.</p>
                <ul className="text-xs mt-1 space-y-0.5">
                  {Object.entries(healScan.collections || {}).map(([coll, info]) => (
                    <li key={coll} className="flex items-center gap-2">
                      <Badge variant="outline">{coll}</Badge>
                      <span>{info.broken} fehlerhaft · {info.healable} reparierbar{info.unhealable > 0 ? ` · ${info.unhealable} manuell prüfen` : ''}</span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            )}
          </Alert>
        )}

        {missingIds && (
          <Alert
            data-testid="missing-ids-result"
            className={missingIds.total_missing === 0
              ? 'border-green-500/50 bg-green-50 dark:bg-green-900/10'
              : 'border-rose-500/50 bg-rose-50 dark:bg-rose-900/10'}
          >
            {missingIds.total_missing === 0
              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
              : <AlertTriangle className="h-4 w-4 text-rose-600" />}
            <AlertTitle>
              {missingIds.total_missing === 0
                ? 'Alle Datensätze haben ein gültiges ID-Feld'
                : `${missingIds.total_missing} Datensatz/Datensätze ohne ID-Feld (Legacy-Import)`}
            </AlertTitle>
            {missingIds.total_missing > 0 && (
              <AlertDescription className="space-y-2">
                <p className="text-xs">
                  Datensätze ohne <code>id</code>-Feld können in Auswertungen (z. B. Reinigungs-Kostenübersicht) zu Fehlern (500) führen. „Jetzt reparieren" vergibt automatisch eine neue ID.
                </p>
                <ul className="text-xs mt-1 space-y-0.5">
                  {Object.entries(missingIds.details || {}).map(([coll, n]) => (
                    <li key={coll} className="flex items-center gap-2">
                      <Badge variant="outline">{coll}</Badge>
                      <span>{n} ohne ID</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  disabled={busy.missingIdsHeal}
                  data-testid="missing-ids-heal-btn"
                  onClick={async () => {
                    if (!window.confirm('Allen Datensätzen ohne ID-Feld automatisch eine neue ID vergeben?')) return;
                    setBusy((p) => ({ ...p, missingIdsHeal: true }));
                    try {
                      const res = await adminAPI.missingIdsHeal();
                      const n = res.data?.total_healed || 0;
                      toast.success(n > 0 ? `${n} Datensatz/Datensätze repariert` : 'Nichts zu reparieren');
                      const rescan = await adminAPI.missingIdsScan();
                      setMissingIds(rescan.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Fehler bei der Reparatur');
                    } finally {
                      setBusy((p) => ({ ...p, missingIdsHeal: false }));
                    }
                  }}
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  Jetzt reparieren
                </Button>
              </AlertDescription>
            )}
          </Alert>
        )}

        {total === 0 ? (
          <Alert className="border-green-500/50 bg-green-50 dark:bg-green-900/10">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle>Alles in Ordnung!</AlertTitle>
            <AlertDescription>
              Es wurden keine Konsistenzprobleme gefunden.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-900/10">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle>{total} Konsistenzproblem{total > 1 ? 'e' : ''} gefunden</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>Jedes Problem hat eine direkte Aktion. Für wiederkehrende Muster gibt es Bulk-Aktionen unten.</p>
              <div className="flex flex-wrap gap-2 pt-2">
                {data?.orphan_employees?.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy.bulkClear}
                    onClick={() => {
                      if (!window.confirm(`${data.orphan_employees.length} defekte Verknüpfungen auf einmal entfernen?`)) return;
                      runAction(
                        'bulkClear',
                        () => adminAPI.bulkClearBrokenLinks(),
                        `${data.orphan_employees.length} Verknüpfung(en) bereinigt`,
                      );
                    }}
                    data-testid="bulk-clear-btn"
                  >
                    <Wrench className="h-4 w-4 mr-1" />
                    Alle defekten Links entfernen
                  </Button>
                )}
                {data?.orphan_users?.length > 1 && (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy.bulkDelete}
                    onClick={() => {
                      if (!window.confirm(`${data.orphan_users.length} verwaiste User wirklich löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
                      runAction(
                        'bulkDelete',
                        () => adminAPI.bulkDeleteOrphanUsers(),
                        `${data.orphan_users.length} User gelöscht`,
                      );
                    }}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Alle verwaisten User löschen
                  </Button>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {(data?.employees_without_user_account || []).length > 0 && (
          <Alert
            className="border-sky-500/50 bg-sky-50 dark:bg-sky-900/10"
            data-testid="no-user-account-alert"
          >
            <UserPlus className="h-4 w-4 text-sky-600" />
            <AlertTitle>
              {data.employees_without_user_account.length} aktive{data.employees_without_user_account.length > 1 ? '' : 'r'} Mitarbeiter ohne Benutzerkonto
            </AlertTitle>
            <AlertDescription>
              Diese Mitarbeiter können sich nicht einloggen. Self-Service-Funktionen wie
              <strong> Stempel-Erinnerungen</strong>, Anträge und der eigene Kalender stehen ihnen daher nicht zur Verfügung.
              Lege bei Bedarf unten pro Person einen Systemzugang an.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 1. Defekte user_id */}
          <IssueSection
            icon={Link2Off}
            color="text-red-500"
            title="Mitarbeiter mit defekter User-Verknüpfung"
            description="Die user_id verweist auf einen User, der nicht mehr existiert. Der Mitarbeiter kann sich nicht einloggen."
            items={data?.orphan_employees || []}
            emptyMessage="Keine defekten Verknüpfungen"
            testId="orphan-employees"
            renderItem={(item) => (
              <div key={item.employee_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.employee_number} · defekte user_id: <code className="font-mono">{item.broken_user_id}</code>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy[`clear-${item.employee_id}`]}
                    onClick={() => runAction(
                      `clear-${item.employee_id}`,
                      () => adminAPI.clearBrokenLink(item.employee_id),
                      'Defekte Verknüpfung entfernt',
                    )}
                    data-testid={`clear-broken-${item.employee_id}`}
                  >
                    <Wrench className="h-4 w-4 mr-1" />
                    Link entfernen
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/employees/${item.employee_id}/edit`)}>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          />

          {/* 2. Passender User, nicht verknüpft */}
          <IssueSection
            icon={Link2}
            color="text-amber-500"
            title="Passender User vorhanden, aber nicht verknüpft"
            description="Die dienstliche E-Mail stimmt mit einem existierenden User überein. Auto-Fix möglich."
            items={data?.unlinked_employees_with_matching_user || []}
            emptyMessage="Keine offenen Verknüpfungen"
            testId="unlinked-employees"
            headerAction={(data?.unlinked_employees_with_matching_user || []).length > 1 && (
              <Button
                size="sm"
                className="mt-2"
                disabled={busy['fix-all-links']}
                onClick={() => runAction(
                  'fix-all-links',
                  async () => {
                    const res = await adminAPI.autoFixLinkAll();
                    const d = res.data || {};
                    if (d.skipped_count) {
                      toast.warning(`${d.linked_count} verknüpft, ${d.skipped_count} übersprungen (Konflikt)`);
                    }
                    return res;
                  },
                  'Alle passenden Konten verknüpft',
                )}
                data-testid="auto-fix-link-all"
              >
                <Wrench className="h-4 w-4 mr-1" />
                {busy['fix-all-links'] ? 'Verknüpfe alle…' : 'Alle automatisch verknüpfen'}
              </Button>
            )}
            renderItem={(item) => (
              <div key={item.employee_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.email}</p>
                </div>
                <Button
                  size="sm"
                  disabled={busy[`fix-${item.employee_id}`]}
                  onClick={() => runAction(
                    `fix-${item.employee_id}`,
                    () => adminAPI.autoFixLink(item.employee_id),
                    `${item.name} verknüpft`,
                  )}
                  data-testid={`auto-fix-${item.employee_id}`}
                >
                  <Wrench className="h-4 w-4 mr-1" />
                  {busy[`fix-${item.employee_id}`] ? 'Verknüpfe…' : 'Auto-Fix'}
                </Button>
              </div>
            )}
          />

          {/* 3. Ohne Standort */}
          <IssueSection
            icon={MapPinOff}
            color="text-orange-500"
            title={'Mitarbeiter ohne Standort (Scope "eigene")'}
            description={'Mitarbeiter mit Scope "eigene", aber ohne Standort — sie sehen in der App nichts.'}
            items={data?.employees_without_location || []}
            emptyMessage="Alle Mitarbeiter haben einen Standort"
            testId="employees-without-location"
            renderItem={(item) => (
              <div key={item.employee_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.employee_number}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={locationSelect[item.employee_id] || ''}
                    onValueChange={(v) => setLocationSelect((p) => ({ ...p, [item.employee_id]: v }))}
                  >
                    <SelectTrigger className="w-48 h-9" data-testid={`location-select-${item.employee_id}`}>
                      <SelectValue placeholder="Standort wählen…" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    disabled={!locationSelect[item.employee_id] || busy[`loc-${item.employee_id}`]}
                    onClick={() => runAction(
                      `loc-${item.employee_id}`,
                      () => adminAPI.assignLocation(item.employee_id, locationSelect[item.employee_id]),
                      'Standort zugewiesen',
                    )}
                    data-testid={`assign-location-${item.employee_id}`}
                  >
                    Zuweisen
                  </Button>
                </div>
              </div>
            )}
          />

          {/* 4. User ohne Mitarbeiterprofil */}
          <IssueSection
            icon={UserX}
            color="text-slate-500"
            title="User ohne Mitarbeiterprofil"
            description="User-Konten, die keinem Mitarbeiter zugeordnet sind (Admins ausgenommen). Entweder Mitarbeiter anlegen oder User löschen."
            items={data?.orphan_users || []}
            emptyMessage="Alle User sind verknüpft"
            testId="orphan-users"
            renderItem={(item) => (
              <div key={item.user_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.email} · {item.role}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const params = new URLSearchParams({
                        business_email: item.email || '',
                        user_id: item.user_id,
                      }).toString();
                      navigate(`/employees/new?${params}`);
                    }}
                    data-testid={`create-employee-${item.user_id}`}
                  >
                    Mitarbeiter anlegen
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy[`del-${item.user_id}`]}
                    onClick={() => {
                      if (!window.confirm(`User "${item.email}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
                      runAction(
                        `del-${item.user_id}`,
                        () => adminAPI.deleteOrphanUser(item.user_id),
                        'User gelöscht',
                      );
                    }}
                    data-testid={`delete-user-${item.user_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          />

          {/* 5. Mitarbeiter ohne Benutzerkonto (kein Self-Service möglich) */}
          <IssueSection
            icon={UserPlus}
            color="text-sky-500"
            title="Mitarbeiter ohne Benutzerkonto"
            description="Aktive Mitarbeiter mit E-Mail, aber ganz ohne Login. Self-Service (Stempel-Erinnerungen, Anträge, eigener Kalender) ist für sie nicht verfügbar."
            items={data?.employees_without_user_account || []}
            emptyMessage="Alle aktiven Mitarbeiter haben ein Benutzerkonto"
            testId="employees-without-user-account"
            renderItem={(item) => (
              <div key={item.employee_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.employee_number ? `${item.employee_number} · ` : ''}{item.email}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={busy[`login-${item.employee_id}`]}
                    onClick={() => handleCreateLogin(item)}
                    data-testid={`create-login-${item.employee_id}`}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    {busy[`login-${item.employee_id}`] ? 'Erstelle…' : 'Login anlegen'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/employees/${item.employee_id}/edit?tab=system`)}
                    data-testid={`login-options-${item.employee_id}`}
                    title="Erweiterte Optionen (Rolle/Standort/eigenes Passwort)"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <Dialog open={!!createdCreds} onOpenChange={(o) => { if (!o) setCreatedCreds(null); }}>
        <DialogContent data-testid="login-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-green-600" />
              Login erstellt
            </DialogTitle>
            <DialogDescription>
              Bitte übermittle diese Zugangsdaten sicher an <strong>{createdCreds?.name}</strong>.
              Das Passwort wird nur <strong>einmalig</strong> angezeigt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border bg-secondary/30 p-3">
              <p className="text-xs text-muted-foreground">E-Mail (Login)</p>
              <p className="font-mono text-sm break-all" data-testid="created-login-email">{createdCreds?.email}</p>
            </div>
            {createdCreds?.password ? (
              <div className="rounded-lg border bg-secondary/30 p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Temporäres Passwort</p>
                  <p className="font-mono text-sm break-all" data-testid="created-login-password">{createdCreds.password}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="copy-credentials-btn"
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      `Login: ${createdCreds.email}\nPasswort: ${createdCreds.password}`
                    );
                    toast.success('Zugangsdaten kopiert');
                  }}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Kopieren
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Ein bestehendes Benutzerkonto wurde verknüpft – es wurde kein neues Passwort erzeugt.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Der Mitarbeiter muss beim ersten Login Passwort und PIN neu setzen.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedCreds(null)} data-testid="close-login-dialog-btn">
              Verstanden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MaybeLayout>
  );
}
